import { useEffect } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import jwt_decode from "jwt-decode";
import { Box, Typography, Button, alpha, useTheme } from "@mui/material";
import { ArrowForwardRounded, AttachFileOutlined, CallOutlined, ForumOutlined, HistoryRounded, RadarOutlined } from "@mui/icons-material";
import { useAuthContext } from "../context/auth";
import useDialogStyles from "../hooks/useDialogStyles";
import { AuthFooterNote } from "../components/auth/AuthCard";
import { BRAND, IS_LAUNCHER } from "../utils/launcher";

const FLIGHT_FEATURES = [
    { icon: RadarOutlined, title: "Live radar", text: "Fused targets from every outpost, SSR first." },
    { icon: HistoryRounded, title: "Replay & review", text: "Step back through any period of recorded flights." },
    { icon: ForumOutlined, title: "Unit chat", text: "Messages, files and calls between units." },
];

const CHAT_FEATURES = [
    { icon: ForumOutlined, title: "Group & direct chat", text: "Talk to your team or one-to-one, with read receipts." },
    { icon: AttachFileOutlined, title: "Share files", text: "Send photos, documents and voice messages." },
    { icon: CallOutlined, title: "Voice & video calls", text: "Call anyone who is online, straight from the chat." },
];
const FEATURES = IS_LAUNCHER ? CHAT_FEATURES : FLIGHT_FEATURES;

// Glass over the background picture (TopLeft paints it on /, /login and /signup)
const glass = (blur = 8) => ({
    backgroundColor: "rgba(5, 20, 40, 0.5)",
    border: "1px solid rgba(14, 2, 2, 0.1)",
    backdropFilter: `blur(${blur}px)`,
});

const Home = () => {
    const { auth, dispatch } = useAuthContext();
    const navigate = useNavigate();

    // Theme
    const theme = useTheme();
    const { goldButtonSx } = useDialogStyles();
    const accent = theme.palette.secondary.main;

    useEffect(() => {
        const params = new URLSearchParams(
            window.location.search
        );

        const token = params.get("token");

        if (!token) return;

        try {
            const decoded = jwt_decode(token);

            dispatch({
                type: "LOGIN",
                payload: {
                    ...decoded.userInfo,
                    accessToken: token,
                },
            });

            window.history.replaceState(
                {},
                document.title,
                window.location.pathname
            );

            navigate("/dashboard", { replace: true });
        } catch (error) {
            console.error("Invalid login token", error);
        }
    }, [dispatch, navigate]);

    if (auth?.accessToken) {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <Box
            sx={{
                minHeight: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                px: 2,
                color: "#fff",
                textAlign: "center",
            }}
        >
            <Box sx={{ flex: 1, width: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", py: 5 }}>
                {/* App icon */}
                <Box
                    component="img"
                    src={BRAND.icon}
                    alt={BRAND.name}
                    sx={{
                        width: { xs: 76, sm: 92, md: 104 },
                        height: "auto",
                        mb: 3,
                        borderRadius: "22px",
                        boxShadow: `0 0 0 1px ${alpha(accent, 0.45)}, 0 12px 34px rgba(0,0,0,0.5), 0 0 40px ${alpha(accent, 0.25)}`,
                    }}
                />

                {/* Eyebrow */}
                <Box
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "999px",
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: accent,
                        ...glass(6),
                        borderColor: alpha(accent, 0.4),
                    }}
                >
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: accent, boxShadow: `0 0 8px ${accent}` }} />
                    {BRAND.eyebrow}
                </Box>

                <Typography
                    component="h1"
                    sx={{
                        fontWeight: 800,
                        fontSize: { xs: "2.75rem", sm: "3.75rem", md: "4.5rem" },
                        lineHeight: 1.05,
                        letterSpacing: "0.06em",
                        color: accent,
                        textShadow: "0 4px 18px rgba(0,0,0,0.55)",
                    }}
                >
                    {BRAND.name}
                </Typography>

                <Typography
                    sx={{
                        mt: 2,
                        mb: 4,
                        maxWidth: 560,
                        fontSize: { xs: "1rem", sm: "1.15rem" },
                        lineHeight: 1.6,
                        color: "rgba(255,255,255,0.82)",
                        textShadow: "0 2px 10px rgba(0,0,0,0.5)",
                    }}
                >
                    {BRAND.tagline}
                </Typography>

                {/* Actions */}
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: "center" }}>
                    <Button
                        component={Link}
                        to="/login"
                        variant="contained"
                        endIcon={<ArrowForwardRounded />}
                        sx={{
                            ...goldButtonSx,
                            height: 46,
                            px: 3.5,
                            fontSize: "0.95rem",
                            textTransform: "none",
                            boxShadow: `0 8px 24px ${alpha(accent, 0.3)}`,
                            transition: "transform 150ms ease, box-shadow 150ms ease, background-color 150ms ease",
                            "&:hover": {
                                backgroundColor: theme.palette.secondary.light,
                                transform: "translateY(-1px)",
                                boxShadow: `0 10px 28px ${alpha(accent, 0.4)}`,
                            },
                        }}
                    >
                        Log in
                    </Button>
                    <Button
                        component={Link}
                        to="/signup"
                        variant="outlined"
                        sx={{
                            height: 46,
                            px: 3.5,
                            borderRadius: 1.5,
                            fontSize: "0.95rem",
                            fontWeight: 600,
                            textTransform: "none",
                            color: "#fff",
                            ...glass(6),
                            borderColor: "rgba(255,255,255,0.35)",
                            "&:hover": { borderColor: accent, color: accent, backgroundColor: "rgba(5, 20, 40, 0.65)" },
                        }}
                    >
                        Create account
                    </Button>
                </Box>

                {/* Features */}
                <Box
                    sx={{
                        mt: { xs: 5, md: 7 },
                        width: "100%",
                        maxWidth: 880,
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                        gap: 1.5,
                    }}
                >
                    {FEATURES.map(({ icon: Icon, title, text }) => (
                        <Box
                            key={title}
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1.5,
                                p: 2,
                                textAlign: "left",
                                borderRadius: 2.5,
                                ...glass(),
                                transition: "border-color 150ms ease, transform 150ms ease",
                                "&:hover": { borderColor: alpha(accent, 0.45), transform: "translateY(-2px)" },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    flexShrink: 0,
                                    display: "grid",
                                    placeItems: "center",
                                    borderRadius: "10px",
                                    color: accent,
                                    backgroundColor: alpha(accent, 0.12),
                                    border: `1px solid ${alpha(accent, 0.3)}`,
                                }}
                            >
                                <Icon fontSize="small" />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#fff" }}>{title}</Typography>
                                <Typography sx={{ mt: 0.25, fontSize: "0.8rem", lineHeight: 1.5, color: "rgba(255,255,255,0.65)" }}>
                                    {text}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Copyright + version: over the dark picture, so light grey in both themes */}
            <AuthFooterNote onDark sx={{ pb: 2.5 }} />
        </Box>
    );
};

export default Home;
